// Pure port of app/components/sidebar/sidebar.js's menu construction (svc.js sidebarViewModel):
// the Role ∩ RoleMenu intersection over raw Core/Menus rows, the extendedAttributes catalog
// (label -> view/enable), and hasAccessToSales() (js/Main.js enumProcess). Never touches window,
// amplify or Knockout: legacyNavState.js supplies plain arrays/flags, legacyMenuRules answers.
//
// Legacy never sorts these lists in practice: sidebar.js does `_.sortBy(menuItems.menuDoc, 'pos')`
// on already-built menuVM instances, whose own field is `.position` (from the catalog), not `.pos` -
// the sort key is always undefined and lodash's stable sort leaves the input order untouched.

// js/Main.js enumProcess: MATERIAL=1, ANIMATION=2, MERCH=5, RENT=7.
const SALES_PROCESS_TYPES = [1, 2, 5, 7];

// sidebarViewModel.prototype.hasAccessToSales().
export function hasAccessToSales(processTypes = []) {
	return (processTypes ?? []).some((type) => SALES_PROCESS_TYPES.includes(type));
}

// svc.js sidebarViewModel.extendedAttributes. `enable` reads plain booleans (never ctx/salesHelper
// directly): legacyNavState.js resolves delegation/sfa/hasAccessToSales/assortOrderActivated itself.
const CATALOG = {
	menuDOC: {
		Documents: { view: 'vwDocFolders', enable: ({ delegation }) => !delegation },
		DocObjectives: { view: 'vwDocObjectives', enable: ({ delegation, sfa }) => !delegation && sfa === false },
		DocWatch: { view: 'vwWatch', enable: ({ delegation, sfa }) => !delegation && sfa === false },
		DocVeille: { view: 'vwVeille', enable: ({ delegation }) => !delegation },
	},
	menuSFA: {
		DocWatch: { view: 'vwWatch', enable: ({ delegation, sfa }) => !delegation && sfa === true },
		DocObjectives: { view: 'vwDocObjectives', enable: ({ delegation, sfa }) => !delegation && sfa === true },
		SFAPromo: { view: 'vwSFAPromo', enable: ({ delegation, sfa }) => !delegation && sfa === true },
	},
	menuSALES: {
		SalesCatalog: { view: 'vwSalesCatalog', enable: ({ sales, assort }) => sales || assort },
		SalesCatalog_Return: { view: 'vwSalesCatalogReturn', enable: ({ assort }) => assort },
		OrdersTracking: { view: 'vwOrdersTracking', enable: ({ delegation, sales }) => !delegation && sales },
		OrdersTrackingAssort: { view: 'vwOrdersTrackingAssort', enable: ({ delegation, assort }) => !delegation && assort },
		OrdersValidation: { view: 'vwOrdersValidation', enable: ({ delegation, sales }) => !delegation && sales },
		OrdersStatement: { view: 'vwOrdersStatement', enable: ({ delegation, sales }) => !delegation && sales },
	},
	menuAgenda: {
		SFAPlanning: { view: 'vwSFAPlanning', enable: ({ sfa }) => sfa === true },
	},
	menuMyPOS: {
		SFAPOS: { view: 'vwSFAPOS', enable: ({ delegation }) => !delegation },
	},
};

// menuHome (svc.js:66) is never fetched/filtered: sidebar.js hardcodes it as a constant pureComputed.
export const HOME_MENU = { appMenu: 'menuHome', label: 'menu_HOME', view: 'widgets' };

function resolveGroup(group, menuRows, allowedIds, predicateInput) {
	const catalog = CATALOG[group];
	return menuRows
		.filter((row) => catalog[row?.label] && allowedIds.has(row.id))
		.map((row) => ({ row, entry: catalog[row.label] }))
		.filter(({ entry }) => entry.enable(predicateInput))
		.map(({ row, entry }) => ({ appMenu: row.target ?? '', label: row.title ?? row.label ?? '', view: entry.view }));
}

/**
 * @param {object} input
 * @param {object[]} input.menuRows              raw Core/Menus rows: { id, label, target, title }
 * @param {number[]} input.roleMenuIds            Core/RolesMenu[0].menuIds for the current sales role
 * @param {boolean}  input.delegation             ctx.delegation
 * @param {boolean}  input.sfa                    salesHelper.isSFAActivated()
 * @param {boolean}  input.hasAccessToSales
 * @param {boolean}  input.assortOrderActivated   salesHelper.isProductAssortOrderActivated()
 * @returns {{ sfa: object[], sales: object[], doc: object[], agenda: object|null, myPos: object|null }}
 *          each entry shaped { appMenu, label, view }, same shape legacyNavState's menuSnapshot() produced.
 */
export function buildMenuGroups({ menuRows = [], roleMenuIds = [], delegation = false, sfa = false, hasAccessToSales: sales = false, assortOrderActivated: assort = false } = {}) {
	const allowedIds = new Set(roleMenuIds ?? []);
	const predicateInput = { delegation, sfa, sales, assort };
	const group = (key) => resolveGroup(key, menuRows, allowedIds, predicateInput);

	return {
		sfa: group('menuSFA'),
		sales: group('menuSALES'),
		doc: group('menuDOC'),
		agenda: group('menuAgenda')[0] ?? null,
		myPos: group('menuMyPOS')[0] ?? null,
	};
}
